import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { BottomNavigation, BottomNavigationAction, Paper } from '@mui/material';

import ArrowLeftIcon from '~/assets/icons/svgs/arrow-left.svg?react';
import ArrowRightIcon from '~/assets/icons/svgs/arrow-right.svg?react';
import MobileDeviceIcon from '~/assets/icons/svgs/mobile-device.svg?react';
import QrCodeIcon from '~/assets/icons/svgs/qr-code-2.svg?react';
import { Loader } from '~/components/atoms/loader/Loader';
import { getStepperIsMicrositeDataValid } from '~/store/reducers/stepepr/selectors';
import { COLORS } from '~/styles/colors';

type MobileFooterStepperProps = {
  handleBack?: () => void;
  handleNext?: () => void;
  showQRModal: () => void;
  showPreviewModal: () => void;
  isNextProcessing?: boolean;
  isQrButtonHidden?: boolean;
  activeStep: number;
  handleStep: (step: number) => void;
  isValid?: boolean;
};

export default function MobileFooterStepper({
  handleBack,
  handleNext,
  showQRModal,
  showPreviewModal,
  isNextProcessing = false,
  isQrButtonHidden = false,
  activeStep,
  handleStep,
  isValid = true,
}: MobileFooterStepperProps) {
  const { t } = useTranslation();

  const isMicrositeDataValid = useSelector(getStepperIsMicrositeDataValid);

  const styles = useStyles();

  const onHandleNext = () => {
    if (handleNext) handleNext();
    else handleStep(activeStep + 1);
  };

  const onHandleBack = () => {
    if (handleBack) handleBack();
    else handleStep(activeStep - 1);
  };

  return (
    <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1 }} elevation={3}>
      <BottomNavigation showLabels sx={{ padding: '4px 0', boxShadow: '0 -4px 10px 0 rgba(0, 0, 0, 0.05)' }}>
        <BottomNavigationAction
          onClick={onHandleBack}
          sx={styles.item}
          icon={<ArrowLeftIcon />}
          label={t('createQrStep1:btn1')}
        />
        {!isQrButtonHidden && (
          <BottomNavigationAction
            onClick={showQRModal}
            sx={styles.item}
            icon={<QrCodeIcon />}
            label={`QR ${t('buttons:preview')}`}
          />
        )}
        <BottomNavigationAction
          onClick={showPreviewModal}
          sx={styles.item}
          icon={<MobileDeviceIcon />}
          label={t('createQrStep1:previewTitle')}
        />
        <BottomNavigationAction
          onClick={isNextProcessing ? undefined : onHandleNext}
          disabled={!isMicrositeDataValid || !activeStep || !isValid || isNextProcessing}
          sx={styles.item}
          icon={isNextProcessing ? <Loader size={22} /> : <ArrowRightIcon />}
          label={t('createQrStep1:btn2')}
        />
      </BottomNavigation>
    </Paper>
  );
}

const useStyles = () => {
  return {
    item: css`
      flex: 1;
      max-width: unset;
      color: ${COLORS.textMain};
      gap: 4px;

      &:not(:last-of-type) {
        border-right: 1px solid ${COLORS.grey200};
      }
      span {
        font-size: 10px;
      }
    `,
  };
};
